import * as React from 'react';
import { useStyles, useFormControlContext, useBoolean, useId, isDefined, useMergedRef } from '@sravni/react-utils';
import { Icon } from '../../Icon/src/Icon.js';
import { Spinner } from '../../Spinner/src/Spinner.js';
import { fixControlledValue } from '../../TextInput/src/TextInput.js';
import modules_dd9d698d from '../styles/Textarea.module.less.js';

const MIN_ROWS = 1;
const MAX_ROWS = 10;
/**
 * @description The Text Area component allow user to type in longer content
 *
 * @component
 * @example
 * ```jsx
 * <Textarea value="content" />
 * ```
 */
const Textarea = React.forwardRef((props, ref) => {
    const { id, value, label, placeholder, resizable, icon, disabled, invalid, readOnly, loading, rows, positionInGroup, directionGroup, background, onFocus, onBlur, onChange, onClick, style, className, ...restProps } = props;
    const cx = useStyles(modules_dd9d698d);
    const formControlContext = useFormControlContext();
    const inputRef = React.useRef();
    const containerRef = React.useRef(null);
    const [focused, setFocused] = useBoolean(false);
    const [filled, setFilled] = useBoolean(value !== undefined && value !== '');
    const [innerRows, setInnerRows] = React.useState(rows || 1);
    const inputId = useId(id ? '' : 'textarea', id);
    const hasIcon = icon || loading;
    const isFocused = focused && !readOnly;
    const isDisabled = formControlContext.disabled || disabled;
    const isInvalid = formControlContext.invalid || invalid;
    const classNames = cx(className, 'container', {
        focused,
        disabled: formControlContext.disabled || disabled,
        invalid: formControlContext.invalid || invalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-label': isDefined(label),
        [`background-${background}`]: background,
        [`group-${directionGroup}`]: directionGroup,
        [`group-${positionInGroup}`]: positionInGroup,
    });
    const classNamesLabel = cx('label', {
        focused: isFocused,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
    });
    const classNamesPlaceholder = cx('placeholder', {
        focused: isFocused,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        withLabel: label && focused,
        hidden: label && !focused,
    });
    const classNamesTextarea = cx('textarea', {
        focused: focused && !readOnly,
        filled,
        disabled: isDisabled,
        invalid: isInvalid,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'with-icon': hasIcon,
        resize: resizable,
        scroll: innerRows === MAX_ROWS,
    });
    const iconElement = React.useMemo(() => {
        if (loading) {
            return React.createElement(Spinner, { className: cx('icon') });
        }
        if (icon) {
            return React.createElement(Icon, { className: cx('icon'), icon: icon });
        }
        return null;
    }, [cx, icon, loading]);
    const labelElement = React.useMemo(() => {
        if (label) {
            return (React.createElement("label", { htmlFor: inputId, className: classNamesLabel }, label));
        }
        return null;
    }, [inputId, label, classNamesLabel]);
    const placeholderElement = React.useMemo(() => {
        if ((label && readOnly) || filled) {
            return null;
        }
        if (placeholder) {
            return (React.createElement("label", { htmlFor: inputId, className: classNamesPlaceholder }, placeholder));
        }
        return null;
    }, [inputId, label, placeholder, filled, readOnly, classNamesPlaceholder]);
    const setRows = React.useCallback(() => {
        if (!inputRef.current) {
            return;
        }
        const textareaLineHeight = 20;
        const previousRows = inputRef.current.rows;
        inputRef.current.rows = rows ?? MIN_ROWS;
        const currentRows = ~~(inputRef.current.scrollHeight / textareaLineHeight);
        if (currentRows === previousRows) {
            inputRef.current.rows = currentRows;
        }
        if (currentRows >= MAX_ROWS) {
            inputRef.current.rows = MAX_ROWS;
            inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }
        currentRows && setInnerRows(currentRows < MAX_ROWS ? currentRows : MAX_ROWS);
    }, [inputRef, rows]);
    const handleClick = React.useCallback((event) => {
        inputRef.current?.focus();
        setFocused.on();
        onClick?.(event);
    }, [onClick, setFocused]);
    const handleFocus = React.useCallback((event) => {
        if (!focused) {
            setFocused.on();
            onFocus?.(event);
        }
    }, [focused, onFocus, setFocused]);
    const handleBlur = React.useCallback((event) => {
        if (event.relatedTarget === containerRef.current)
            return;
        setFocused.off();
        onBlur?.(event);
    }, [onBlur, setFocused]);
    const handleChange = React.useCallback((event) => {
        resizable && setRows();
        onChange?.(event);
    }, [resizable, setRows, onChange]);
    const handleContainerBlur = (event) => {
        // needs to prevent extra onBlur callback executing on Textarea wrapper
        const relTarget = event.relatedTarget;
        if (containerRef.current?.contains(relTarget)) {
            event.stopPropagation();
        }
    };
    const handleContainerFocus = (event) => {
        // needs to prevent extra onFocus callback executing on Textarea wrapper
        const relTarget = event.relatedTarget;
        if (relTarget === containerRef.current) {
            event.stopPropagation();
        }
    };
    React.useEffect(() => {
        if (value !== undefined && value !== '') {
            setFilled.on();
        }
        else {
            setFilled.off();
        }
    }, [setFilled, value]);
    React.useEffect(() => {
        resizable && setRows();
    }, [resizable, value, setRows]);
    return (React.createElement("div", { ref: containerRef, "data-qa": "Textarea", className: classNames, style: style, onClick: handleClick, onBlur: handleContainerBlur, onFocus: handleContainerFocus, tabIndex: -1 },
        React.createElement("textarea", { id: inputId, spellCheck: "false", rows: innerRows, ref: useMergedRef(inputRef, ref), value: fixControlledValue(value), disabled: formControlContext.disabled || disabled, readOnly: readOnly, className: classNamesTextarea, onBlur: handleBlur, onFocus: handleFocus, onChange: handleChange, ...restProps }),
        labelElement,
        placeholderElement,
        iconElement));
});
Textarea.defaultProps = {
    rows: 1,
    resizable: true,
};
Textarea.displayName = 'Textarea';

export { Textarea };
//# sourceMappingURL=Textarea.js.map
